import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { PageHeader, Modal, ConfirmDialog, EmptyState, useToast, usePagination, Pagination, useSort, SortTh, SearchableSelect, FilterBar, FilterSelect, TableSkeletonRows } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { fmtDate, fmtDateTime } from '../utils/format';

// ── AUDIT LOGS ────────────────────────────────────────────────────────────────
// Theme-aware tokens (reused from elsewhere in the app) — all verified ≥4.5:1
// on both --sms-sur (light) and --sms-sur (dark), unlike the raw hex this replaced.
const ACTION_COLOR = {
  login:  'var(--sms-dot-late)',    // amber/gold — 4.92:1 light / 12.6:1 dark
  logout: 'var(--sms-txt2)',        // neutral grey — 10.3:1 light / 5.58:1 dark
  create: 'var(--sms-dot-present)', // green — 8.4:1 light / 10.9:1 dark
  update: 'var(--sms-acc)',         // blue — 5.2:1 light / 6.6:1 dark
  delete: 'var(--sms-dot-absent)',  // red — 7.3:1 light / 10.5:1 dark
};
const ACTION_ICON  = { login:'bi-box-arrow-in-right', logout:'bi-box-arrow-right', create:'bi-plus-circle', update:'bi-pencil-square', delete:'bi-trash' };

function localDateISO(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function AuditLogsPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [action,  setAction]  = useState('');
  const [module,  setModule]  = useState('');
  const [from,    setFrom]    = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [to,      setTo]      = useState(() => localDateISO());
  const [search,  setSearch]  = useState('');

  const load = () => {
    setLoading(true);
    const params = { limit: 100 };
    if (action) params.action   = action;
    if (module) params.module   = module;
    if (from)   params.from     = from;
    if (to)     params.to       = to;
    if (search) params.search   = search;
    api.get('/audit-logs', { params })
      .then(r => setRows(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [action, module, from, to, search]);

  const { sorted: auditSorted, sortKey: auditSK, sortDir: auditSD, toggleSort: auditSort } = useSort(rows);
  const { paged: auditPaged, page: auditPage, setPage: setAuditPage, pageSize: auditPageSize, setPageSize: setAuditPageSize, totalPages: auditTotalPages, total: auditTotal, start: auditStart } = usePagination(auditSorted);

  const MODULES = ['auth','students','intakes','users','activities','disciplinary','alumni','requests','permissions','academic_performance'];

  return (
    <>
      <PageHeader title="Audit Logs" subtitle={`${auditTotal} entries`} />
      <div className="sms-card mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-6 col-sm-3 col-lg-2">
            <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', marginBottom: 4 }}>Action</label>
            <SearchableSelect value={action} onChange={e => setAction(e.target.value)} placeholder="All"
              options={['login','logout','create','update','delete'].map(a=>({value:a,label:a.charAt(0).toUpperCase()+a.slice(1)}))} />
          </div>
          <div className="col-6 col-sm-3 col-lg-2">
            <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', marginBottom: 4 }}>Module</label>
            <SearchableSelect value={module} onChange={e => setModule(e.target.value)} placeholder="All"
              options={MODULES.map(m=>({value:m,label:m}))} />
          </div>
          <div className="col-6 col-sm-3 col-lg-2">
            <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', marginBottom: 4 }}>From</label>
            <input type="date" className="sms-form-control" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div className="col-6 col-sm-3 col-lg-2">
            <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', marginBottom: 4 }}>To</label>
            <input type="date" className="sms-form-control" value={to} onChange={e => setTo(e.target.value)} />
          </div>
          <div className="col-12 col-lg-4">
            <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', marginBottom: 4 }}>Search</label>
            <input className="sms-form-control" placeholder="Search description, user, IP…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && rows.length === 0 ? <EmptyState icon="bi-shield-check" message="No audit logs found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead>
                <tr>
                  <SortTh label="Action" field="action" sortKey={auditSK} sortDir={auditSD} onSort={auditSort} style={{width:110}}/>
                  <SortTh label="Module" field="module" sortKey={auditSK} sortDir={auditSD} onSort={auditSort}/>
                  <th>Description</th>
                  <SortTh label="User" field="username" sortKey={auditSK} sortDir={auditSD} onSort={auditSort}/>
                  <th>IP</th>
                  <SortTh label="Timestamp" field="created_at" sortKey={auditSK} sortDir={auditSD} onSort={auditSort}/>
                </tr>
              </thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={6} /> : auditPaged.map(r => (
                  <tr key={r.id}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                        <i className={`bi ${ACTION_ICON[r.action] || 'bi-circle'}`} style={{ color: ACTION_COLOR[r.action] || 'var(--sms-txt2)' }} />
                        <span style={{ color: ACTION_COLOR[r.action] || 'var(--sms-txt2)', fontWeight: 500 }}>{r.action}</span>
                      </span>
                    </td>
                    <td><span className="badge rounded-pill badge-inactive" style={{ fontSize: 12 }}>{r.module}</span></td>
                    <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</td>
                    <td>{r.username}</td>
                    <td>{r.ip_address}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            {!loading && <Pagination page={auditPage} totalPages={auditTotalPages} total={auditTotal} pageSize={auditPageSize} setPage={setAuditPage} setPageSize={setAuditPageSize} start={auditStart}/>}
          </>
        )}
      </div>
    </>
  );
}

// ── USERS (Super Admin only) ──────────────────────────────────────────────────
// Matches your actual users table: name, email, username, password, role,
// phone, dept_name, dept_code, is_active, ms365_id, avatar_url, last_login
const EMPTY_NEW_USER = { username: '', email: '', name: '', password: '', role: 'finance', dept_name: '', phone: '' };
const ROLE_BADGE_CLASS = { super_admin: 'badge-absent', admin: 'badge-active', finance: 'badge-late' };

export function UsersPage() {
  const toast = useToast();
  const { user: me } = useAuth();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_NEW_USER);
  const [saving,  setSaving]  = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/users').then(r => setRows(r.data.data)).catch(() => toast('Load failed','error')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd = () => { setNewUser(EMPTY_NEW_USER); setShowAdd(true); };
  const handleCreate = async () => {
    if (!newUser.username.trim() || !newUser.email.trim() || !newUser.name.trim() || !newUser.password.trim()) {
      toast('Username, email, name and password are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.post('/users', newUser);
      toast('User created', 'success');
      setShowAdd(false);
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to create user', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Never show the currently logged-in admin's own account, even as super_admin
  const others = rows.filter(u => u.id !== me?.id);

  const [userSearch, setUserSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('active');
  const [roleFilter, setRoleFilter] = React.useState('');
  const [deptFilter, setDeptFilter] = React.useState('');
  const deptOptions = [...new Set(others.map(u => u.dept_name).filter(Boolean))].sort()
    .map(d => ({ value: d, label: d }));
  const userFiltered = others.filter(r =>
    (!userSearch || (r.username||'').toLowerCase().includes(userSearch.toLowerCase()) || (r.full_name||'').toLowerCase().includes(userSearch.toLowerCase()) || (r.email||'').toLowerCase().includes(userSearch.toLowerCase())) &&
    (!statusFilter || (statusFilter === 'active' ? !!r.is_active : !r.is_active)) &&
    (!roleFilter || r.role === roleFilter) &&
    (!deptFilter || r.dept_name === deptFilter)
  );
  const { sorted: userSorted, sortKey: userSK, sortDir: userSD, toggleSort: userSort } = useSort(userFiltered, 'last_login', 'desc');
  const { paged: userPaged, page: userPage, setPage: setUserPage, pageSize: userPageSize, setPageSize: setUserPageSize, totalPages: userTotalPages, total: userTotal, start: userStart } = usePagination(userSorted);

  return (
    <>
      <PageHeader title="User Management" subtitle="Accessible by Super Admin Only"
        actions={
          <button className="sms-btn sms-btn-primary" onClick={openAdd}>
            <i className="bi bi-person-plus" /> Add user
          </button>
        } />

      <FilterBar search={userSearch} onSearch={setUserSearch} searchPlaceholder="Search username, name, email…"
        onClear={() => { setUserSearch(''); setStatusFilter(''); setRoleFilter(''); setDeptFilter(''); }}>
        <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="All statuses"
          options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
        <FilterSelect value={roleFilter} onChange={setRoleFilter} placeholder="All roles"
          options={[{ value: 'super_admin', label: 'Super Admin' }, { value: 'admin', label: 'Admin' }, { value: 'staff', label: 'Staff' }, { value: 'trainer', label: 'Trainer' }, { value: 'finance', label: 'Finance' }]} />
        <FilterSelect value={deptFilter} onChange={setDeptFilter} placeholder="All depts" options={deptOptions} />
      </FilterBar>

      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && userFiltered.length === 0 ? <EmptyState icon="bi-person-gear" message="No users found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Username</th>
                  <SortTh label="Name" field="full_name" sortKey={userSK} sortDir={userSD} onSort={userSort}/>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Dept</th>
                  <SortTh label="Status" field="is_active" sortKey={userSK} sortDir={userSD} onSort={userSort}/>
                  <SortTh label="Last Login" field="last_login" sortKey={userSK} sortDir={userSD} onSort={userSort}/>
                </tr>
              </thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={8} /> : userPaged.map((u, idx) => (
                  <tr key={u.id}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{userStart+idx+1}</td>
                    <td style={{ fontWeight: 500 }}>{u.username}</td>
                    <td>{u.full_name}</td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`badge rounded-pill ${ROLE_BADGE_CLASS[u.role] || 'badge-inactive'}`} style={{ fontSize: 12 }}>
                        {u.role.replace('_',' ')}
                      </span>
                    </td>
                    <td>{u.dept_name || '—'}</td>
                    <td>
                      <span className={`badge rounded-pill ${u.is_active ? 'badge-present' : 'badge-inactive'}`} style={{ fontSize: 12 }}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {u.last_login ? fmtDateTime(u.last_login) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            {!loading && <Pagination page={userPage} totalPages={userTotalPages} total={userTotal} pageSize={userPageSize} setPage={setUserPage} setPageSize={setUserPageSize} start={userStart}/>}
          </>
        )}
      </div>

      <Modal show={showAdd} onClose={() => setShowAdd(false)} title="Add User">
        <div className="row g-3">
          <div className="col-6">
            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>Full name</label>
            <input className="sms-form-control" value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))} />
          </div>
          <div className="col-6">
            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>Username</label>
            <input className="sms-form-control" value={newUser.username} onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} />
          </div>
          <div className="col-6">
            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>Email</label>
            <input type="email" className="sms-form-control" value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} />
          </div>
          <div className="col-6">
            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>Password</label>
            <input type="password" className="sms-form-control" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} />
          </div>
          <div className="col-6">
            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>Role</label>
            <select className="sms-form-control" value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}>
              <option value="admin">Admin</option>
              <option value="staff">Staff</option>
              <option value="trainer">Trainer</option>
              <option value="finance">Finance</option>
            </select>
          </div>
          <div className="col-6">
            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>Department (optional)</label>
            <input className="sms-form-control" value={newUser.dept_name} onChange={e => setNewUser(u => ({ ...u, dept_name: e.target.value }))} />
          </div>
        </div>
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button type="button" className="sms-btn sms-btn-outline" onClick={() => setShowAdd(false)} disabled={saving}>Cancel</button>
          <button type="button" className="sms-btn sms-btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating…' : <><i className="bi bi-check-lg" /> Create user</>}
          </button>
        </div>
      </Modal>
    </>
  );
}

// ── APPROVER (TAEPP requests and verified claims) ──────────────────────────────
// Super admin picks the one person who approves or rejects trainers' TAEPP requests and
// signs to approve verified claims. Shown on the Approval Settings page.
function TaeppApproverCard() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approverId, setApproverId] = useState('');
  const [savedId, setSavedId] = useState(null);
  const [designation, setDesignation] = useState('');
  const [savedDesignation, setSavedDesignation] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    api.get('/trainers/taepp-approver')
      .then(r => {
        const d = r.data.data;
        setCandidates(d.candidates || []);
        setSavedId(d.approver_id);
        setApproverId(d.approver_id ? String(d.approver_id) : '');
        setDesignation(d.designation || '');
        setSavedDesignation(d.designation || '');
        setPendingCount(d.pending_count || 0);
      })
      .catch(() => toast('Failed to load TAEPP approver', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/trainers/taepp-approver', { approver_id: approverId, designation: designation.trim() });
      setSavedId(Number(approverId));
      setSavedDesignation(designation.trim());
      setDesignation(designation.trim());
      toast('TAEPP approver updated', 'success');
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to update TAEPP approver', 'error');
    } finally {
      setSaving(false);
    }
  };

  const current = candidates.find(c => String(c.id) === String(savedId));
  const unchanged = String(savedId || '') === approverId && savedDesignation === designation.trim();

  return (
    <div className="sms-card taepp" style={{ flex: '1 1 420px', maxWidth: 560 }}>
        <div className="tp-title" style={{ marginBottom: 16 }}>
          <span className="tp-ic" aria-hidden="true"><i className="bi bi-person-check" /></span>
          <span>
            <span className="tp-ttl">Approver</span>
            <span className="tp-sub">The one person who approves TAEPP requests and signs to approve verified claims.</span>
          </span>
        </div>

        <label htmlFor="taepp-approver" className="tp-label">Approver</label>
        <select id="taepp-approver" className="sms-form-control" style={{ width: '100%' }} disabled={loading}
          value={approverId} onChange={e => setApproverId(e.target.value)}>
          <option value="">Select a person</option>
          {candidates.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
        </select>

        <label htmlFor="taepp-designation" className="tp-label" style={{ marginTop: 14 }}>Designation</label>
        <input id="taepp-designation" type="text" className="sms-form-control" style={{ width: '100%' }}
          maxLength={100} disabled={loading} placeholder="e.g. Head, Training Operations"
          value={designation} onChange={e => setDesignation(e.target.value)} />
        <div className="tp-card-sub" style={{ marginTop: 4 }}>Printed under the approver's name on the approved TAEPP.</div>

        {!loading && (
          <div className={`tp-card${current ? '' : ' muted'}`} role="status" style={{ marginTop: 14 }}>
            <i className={`bi ${current ? 'bi-person-check' : 'bi-exclamation-circle'}`} aria-hidden="true" />
            <div>
              {current ? (
                <>
                  <div className="tp-card-main">Current approver: {current.full_name}{savedDesignation ? ` — ${savedDesignation}` : ''}</div>
                  <div className="tp-card-sub">{pendingCount} pending request{pendingCount === 1 ? '' : 's'} waiting</div>
                </>
              ) : (
                <>
                  <div className="tp-card-main">No approver is set yet</div>
                  <div className="tp-card-sub">TAEPP requests can't be decided until you pick one.</div>
                </>
              )}
            </div>
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <div className="tp-note">
            <i className="bi bi-info-circle" aria-hidden="true" />
            <span>Changing the approver moves any pending TAEPP requests and verified claims to the new person.</span>
          </div>
        </div>

        <div className="tp-actions">
          <button type="button" className="tp-btn primary" disabled={saving || loading || !approverId || !designation.trim() || unchanged} onClick={save}>
            {saving ? 'Saving…' : <><i className="bi bi-check-lg" aria-hidden="true" />Save Approver</>}
          </button>
        </div>
      </div>
  );
}

// ── CLAIM VERIFIERS ──────────────────────────────────────────────────────────
// Super admin adds the email addresses that are told when a trainer submits a claim.
// Anyone with access to the claims queue can still verify a claim; this only decides who
// is emailed. Shown on the Approval Settings page.
const EMAIL_FORMAT = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const MAX_VERIFIER_EMAILS = 20;

function ClaimVerifiersCard() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState([]);
  const [saved, setSaved] = useState('');   // the saved list, to tell if anything changed
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/trainers/claim-verifiers')
      .then(r => { const list = r.data.data.emails || []; setEmails(list); setSaved(list.join(',')); })
      .catch(() => toast('Failed to load claim verifiers', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const add = () => {
    const value = draft.trim().toLowerCase();
    if (!value) return;
    if (!EMAIL_FORMAT.test(value)) { setError('Enter a valid email address.'); return; }
    if (emails.includes(value)) { setError('That email address is already in the list.'); return; }
    if (emails.length >= MAX_VERIFIER_EMAILS) { setError(`You can add up to ${MAX_VERIFIER_EMAILS} email addresses.`); return; }
    setEmails(prev => [...prev, value]);
    setDraft('');
    setError('');
  };
  const remove = email => setEmails(prev => prev.filter(e => e !== email));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/trainers/claim-verifiers', { emails });
      setSaved(emails.join(','));
      toast('Claim verifiers updated', 'success');
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to update claim verifiers', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sms-card taepp" style={{ flex: '1 1 420px', maxWidth: 560 }}>
      <div className="tp-title" style={{ marginBottom: 16 }}>
        <span className="tp-ic" aria-hidden="true"><i className="bi bi-envelope-check" /></span>
        <span>
          <span className="tp-ttl">Claim Verifiers</span>
          <span className="tp-sub">The email addresses told when a trainer submits a claim.</span>
        </span>
      </div>

      <label htmlFor="claim-verifier-email" className="tp-label">Add an Email Address</label>
      <div className="d-flex gap-2">
        <input id="claim-verifier-email" type="email" className="sms-form-control" style={{ flex: 1 }} disabled={loading}
          placeholder="name@company.com" autoComplete="off" aria-invalid={!!error}
          value={draft} onChange={e => { setDraft(e.target.value); setError(''); }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" className="tp-btn" disabled={loading || !draft.trim()} onClick={add}>
          <i className="bi bi-plus-lg" aria-hidden="true" />Add
        </button>
      </div>
      {error && <div className="tp-error"><i className="bi bi-exclamation-circle" aria-hidden="true" /><span>{error}</span></div>}

      <div style={{ marginTop: 14 }}>
        {loading ? <div className="tp-hint">Loading…</div> : emails.length === 0 ? (
          <div className="tp-card muted" role="status">
            <i className="bi bi-envelope-slash" aria-hidden="true" />
            <div>
              <div className="tp-card-main">No email addresses yet</div>
              <div className="tp-card-sub">Nobody is emailed when a claim is submitted.</div>
            </div>
          </div>
        ) : (
          <div role="list" aria-label="Claim verifier emails" style={{ display: 'flex', flexDirection: 'column' }}>
            {emails.map(email => (
              <div key={email} role="listitem" className="tp-row" style={{ padding: '7px 4px', borderBottom: '1px solid var(--tp-bd2)', flexWrap: 'nowrap' }}>
                <span style={{ wordBreak: 'break-all', color: 'var(--tp-tx)' }}>{email}</span>
                <button type="button" className="tp-btn sm text-bad" aria-label={`Remove ${email}`} onClick={() => remove(email)}>
                  <i className="bi bi-x-lg" aria-hidden="true" />Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        <div className="tp-note">
          <i className="bi bi-info-circle" aria-hidden="true" />
          <span>Anyone with access to the claims queue can still verify a claim. This only decides who is emailed, and the first to sign verifies it.</span>
        </div>
      </div>

      <div className="tp-actions">
        <button type="button" className="tp-btn primary" disabled={saving || loading || emails.join(',') === saved} onClick={save}>
          {saving ? 'Saving…' : <><i className="bi bi-check-lg" aria-hidden="true" />Save Verifiers</>}
        </button>
      </div>
    </div>
  );
}

// ── APPROVAL SETTINGS ────────────────────────────────────────────────────────
// One page for everything about who signs and who is told: the approver, and the staff
// emailed when a claim is submitted.
export function ApprovalSettingsPage() {
  return (
    <>
      <PageHeader title="Approval Settings" subtitle="Who approves trainer claims and TAEPP requests, and who is told about new claims" />
      <div className="d-flex flex-wrap gap-3 align-items-start">
        <TaeppApproverCard />
        <ClaimVerifiersCard />
      </div>
    </>
  );
}
